/**
 * Media API - Moderation
 *
 * Content reporting and moderation endpoints.
 */

import { z } from 'zod'
import { MediaReportReasonSchema, MediaReportStatusSchema, ResolveStatusSchema, ReportResolutionSchema } from './common'

// ============================================================================
// POST /v1/media/:id/report (Report Media)
// ============================================================================

export const ReportMediaRequestSchema = z.object({
  id: z.string().min(1),
  reason: MediaReportReasonSchema,
  description: z.string().max(2000).optional()
})

export type ReportMediaRequest = z.infer<typeof ReportMediaRequestSchema>

export const ReportMediaResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    id: z.string(),
    status: z.literal('PENDING'),
    createdAt: z.date()
  })
})

export type ReportMediaResponse = z.infer<typeof ReportMediaResponseSchema>

// ============================================================================
// GET /v1/media/reports (List Reports)
// ============================================================================

export const MediaListReportsRequestSchema = z.object({
  status: MediaReportStatusSchema.optional(),
  limit: z.coerce.number().min(1).max(100).optional(),
  offset: z.coerce.number().min(0).optional()
})

export type MediaListReportsRequest = z.infer<typeof MediaListReportsRequestSchema>

export const MediaReportItemSchema = z.object({
  id: z.string(),
  mediaId: z.string(),
  media: z
    .object({
      id: z.string(),
      thumbnailUrl: z.string().nullable(),
      title: z.string().nullable(),
      cloudinaryThumbnailUrl: z.string().nullable().optional(),
      cloudinaryUrl: z.string().nullable().optional()
    })
    .optional(),
  reportedBy: z.union([
    z.string(),
    z.object({
      id: z.string(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable()
    })
  ]),
  reporter: z
    .object({
      id: z.string(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      photoUrl: z.string().nullable().optional()
    })
    .optional(),
  reason: z.string(),
  description: z.string().nullable(),
  status: MediaReportStatusSchema,
  resolution: ReportResolutionSchema.nullable().optional(),
  resolvedAt: z.date().nullable().optional(),
  resolvedBy: z.string().nullable().optional(),
  createdAt: z.date()
})

export type MediaReportItem = z.infer<typeof MediaReportItemSchema>

export const MediaListReportsResponseSchema = z.object({
  message: z.string(),
  data: z.array(MediaReportItemSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    pendingCount: z.number()
  })
})

export type MediaListReportsResponse = z.infer<typeof MediaListReportsResponseSchema>

// ============================================================================
// GET /v1/media/reports/:id (Get Report)
// ============================================================================

export const GetReportRequestSchema = z.object({
  id: z.string().min(1)
})

export type GetReportRequest = z.infer<typeof GetReportRequestSchema>

export const GetReportResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    id: z.string(),
    media: z.object({
      id: z.string(),
      thumbnailUrl: z.string().nullable(),
      title: z.string().nullable(),
      description: z.string().nullable(),
      mediaType: z.string(),
      uploadedBy: z.object({
        id: z.string(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable()
      })
    }),
    reportedBy: z.object({
      id: z.string(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable()
    }),
    reason: z.string(),
    description: z.string().nullable(),
    status: z.string(),
    resolution: z.string().nullable(),
    adminNotes: z.string().nullable(),
    resolvedBy: z
      .object({
        id: z.string(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable()
      })
      .nullable(),
    resolvedAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date()
  })
})

export type GetReportResponse = z.infer<typeof GetReportResponseSchema>

// ============================================================================
// PATCH /v1/media/reports/:id/resolve (Resolve Report)
// ============================================================================

export const MediaResolveReportRequestSchema = z.object({
  // id is in URL path, make optional for body
  id: z.string().min(1).optional(),
  // status defaults to RESOLVED when calling this endpoint
  status: ResolveStatusSchema.optional(),
  resolution: ReportResolutionSchema,
  adminNotes: z.string().max(2000).optional()
})

export type MediaResolveReportRequest = z.infer<typeof MediaResolveReportRequestSchema>

export const MediaResolveReportResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    id: z.string(),
    status: z.string(),
    resolution: z.string(),
    resolvedAt: z.date()
  })
})

export type MediaResolveReportResponse = z.infer<typeof MediaResolveReportResponseSchema>
